import posthog from 'posthog-js';
import { redactEventUrls } from '@helpers/analytics-urls';
import { getActiveAccountId } from '@helpers/auth';
import logger from './logger';

const IMPERSONATION_STORAGE_KEY = 'pl-ph-impersonate';
const ACCOUNT_GROUP_TYPE = 'account';
const PERSONAL_DATA_ATTRIBUTES = [
    'aria-label',
    'aria-valuetext',
    'alt',
    'title',
    'href',
    'src',
    'srcset',
    'style',
    'download',
    'action',
    'value',
    'placeholder',
    'data-value',
];

class PostHogManager {
    constructor() {
        this.initialized = false;
    }

    init() {
        if (this.initialized) {
            logger.warn('PostHog already initialized');
            return true;
        }

        const apiKey = import.meta.env.REACT_APP_POSTHOG_API_KEY;
        const apiHost = import.meta.env.REACT_APP_POSTHOG_API_HOST;
        const uiHost = import.meta.env.REACT_APP_POSTHOG_UI_HOST;

        if (!apiKey || !apiHost || !uiHost) {
            logger.warn('PostHog initialization skipped: Missing API key or API/UI host in environment variables');
            return false;
        }

        try {
            posthog.init(apiKey, {
                api_host: apiHost,
                ui_host: uiHost,
                capture_pageview: false,
                autocapture: { element_attribute_ignorelist: PERSONAL_DATA_ATTRIBUTES },
                capture_dead_clicks: false,
                mask_all_text: true,
                before_send: redactEventUrls,
                cookie_persisted_properties: ['is_impersonated', 'impersonator_email'],
            });

            this.initialized = true;
            logger.success('PostHog initialized successfully');

            return true;
        } catch (error) {
            logger.error('Failed to initialize PostHog:', error);
            return false;
        }
    }

    identify(user, impersonation) {
        if (!user?.id) {
            logger.error('identify: user id is required');
            return;
        }
        if (impersonation) {
            this._setImpersonationStorage(user.id, impersonation.impersonatorEmail || null);
        } else {
            this._clearImpersonationStorage();
        }
        if (!this.initialized) {
            logger.warn('PostHog not initialized. Skipping identify.');
            return;
        }
        try {
            const userId = String(user.id);
            const isImpersonated = Boolean(impersonation);
            let identityChanged = isImpersonated;

            if (isImpersonated) {
                // INFO: Isolate impersonation session from any previous user
                posthog.reset(true);
                posthog.register({
                    is_impersonated: true,
                    impersonator_email: impersonation.impersonatorEmail || null,
                });
                posthog.identify(userId);
            } else {
                // INFO: Unregister super props in case impersonation session wasn't terminated properly
                posthog.unregister('is_impersonated');
                posthog.unregister('impersonator_email');

                const phUserId = posthog.get_property('$user_id');
                identityChanged = Boolean(phUserId) && String(phUserId) !== userId;
                // INFO: If this browser is currently identified as a DIFFERENT user, isolate first
                if (identityChanged) {
                    posthog.reset(true);
                }
                if (!phUserId || identityChanged) {
                    posthog.identify(userId);
                }
            }

            this._groupByAccount(user.accountId > 0 ? user.accountId : getActiveAccountId());
            // INFO: Re-capture pageview under the new identity since reset() severed the chain.
            // Anonymous users don't need this — PostHog merges the anonymous pageview automatically.
            if (identityChanged) {
                this.capturePageView(window.location.pathname + window.location.search);
            }

            logger.success(`PostHog: User identified as ${userId}${isImpersonated ? ' (impersonated)' : ''}`);
        } catch (error) {
            logger.error('PostHog identify error:', error);
        }
    }

    isImpersonating() {
        return this._getImpersonationStorage()?.isImpersonated === true;
    }

    reset() {
        const isImpersonated = this._getImpersonationStorage()?.isImpersonated === true;
        this._clearImpersonationStorage();
        if (!this.initialized) {
            logger.warn('PostHog not initialized. Skipping reset.');
            return;
        }
        try {
            // INFO: Reset PostHog state and clears impersonation data
            posthog.reset(isImpersonated);

            logger.success(`PostHog: Session reset${isImpersonated ? ' (impersonation ended)' : ''}`);
        } catch (error) {
            logger.error('PostHog reset error:', error);
        }
    }

    capturePageView(path) {
        if (!this.initialized) return;

        try {
            posthog.capture('$pageview', {
                $current_url: window.location.origin + path,
            });
        } catch (error) {
            logger.error('PostHog pageview error:', error);
        }
    }

    setAccount(accountId) {
        if (!this.initialized) return;

        try {
            this._groupByAccount(accountId);
        } catch (error) {
            logger.error('PostHog account group error:', error);
        }
    }

    _groupByAccount(accountId) {
        if (accountId) {
            posthog.group(ACCOUNT_GROUP_TYPE, String(accountId));
        }
    }

    capture(event, properties) {
        if (!this.initialized) return;

        try {
            posthog.capture(event, properties);
        } catch (error) {
            logger.error(`PostHog capture error for "${event}":`, error);
        }
    }

    _setImpersonationStorage(targetUserId, impersonatorEmail) {
        try {
            localStorage.setItem(
                IMPERSONATION_STORAGE_KEY,
                JSON.stringify({
                    isImpersonated: true,
                    targetUserId,
                    impersonatorEmail,
                }),
            );
        } catch (error) {
            logger.error('Failed to persist impersonation state:', error);
        }
    }

    _getImpersonationStorage() {
        try {
            const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            logger.error('Failed to read impersonation state:', error);
            return null;
        }
    }

    _clearImpersonationStorage() {
        try {
            localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
        } catch (error) {
            logger.error('Failed to clear impersonation state:', error);
        }
    }
}

export default new PostHogManager();
